/**
 * Must stay the first import: rewrites the URL (MBC session handoff via
 * `?token=`) before New Relic/PostHog capture it and before the router boots.
 */
import '#utils/token-bootstrap.ts';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { CssBaseline } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFnsV2';
import { LicenseInfo } from '@mui/x-license';
import { PostHogProvider } from '@posthog/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { SnackbarProvider } from 'notistack';
import posthog from 'posthog-js';
import { RouterProvider } from 'react-router';

import { config } from '#config.ts';
import '#lib/new-relic.ts';
import { ThemeModeProvider } from '#lib/ThemeProvider';
import { queryClient } from './lib/react-query/client.ts';
import { createRouter } from './routes/router.tsx';

import './styles.css';

/**
 * Perfectly fine to be hard-coded.
 * @see https://github.com/mui/mui-x/issues/12308#issuecomment-1976362879
 * @see https://github.com/mui/mui-x/issues/12308#issuecomment-1978327132
 * @see https://mui.com/x/introduction/licensing/#environment-variable-with-next-js
 */
LicenseInfo.setLicenseKey(config.MUI_LICENSE_KEY);

/**
 * No BAA is in place with PostHog, so we keep analytics limited:
 * - Pageviews/pageleaves stay on for route-level usage. `before_send` strips unsafe query params
 *   from URLs before events leave the browser.
 * - Autocapture, dead clicks, rage clicks, heatmaps, and session recording are off so PostHog does
 *   not collect DOM text, element metadata, click maps, or replay data.
 * - `person_profiles: 'identified_only'` keeps usage person-less until we explicitly identify a
 *   logged-in user.
 */
const safeUrlQueryParams = new Set(['tab', 'page', 'pageSize', 'sortBy']);
const urlPropertiesToRedact = [
  '$current_url',
  '$referrer',
  '$initial_current_url',
  '$initial_referrer',
] as const;

function redactUrlQueryParams(url: string): string {
  try {
    const parsed = new URL(url);
    const safeParams = new URLSearchParams();
    parsed.searchParams.forEach((value, key) => {
      if (safeUrlQueryParams.has(key)) {
        safeParams.set(key, value);
      }
    });
    parsed.search = safeParams.toString();
    return parsed.toString();
  } catch {
    const queryStartIndex = url.indexOf('?');
    return queryStartIndex === -1 ? url : url.slice(0, queryStartIndex);
  }
}

posthog.init(config.POSTHOG_TOKEN, {
  api_host: config.POSTHOG_HOST,
  defaults: '2026-01-30',
  autocapture: false,
  capture_pageview: true,
  capture_pageleave: true,
  capture_dead_clicks: false,
  rageclick: false,
  enable_heatmaps: false,
  disable_session_recording: true,
  person_profiles: 'identified_only',
  before_send: (event) => {
    if (event) {
      for (const property of urlPropertiesToRedact) {
        const value: unknown = event.properties[property];
        if (typeof value === 'string') {
          event.properties[property] = redactUrlQueryParams(value);
        }
      }
    }
    return event;
  },
});

/**
 * Reset on every load so a prior user's identity never carries into a later session on shared
 * devices. Authenticated loaders identify the current logged-in user again after `/user/me`
 * succeeds.
 */
posthog.reset();

const router = createRouter(queryClient, posthog);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <PostHogProvider client={posthog}>
      <QueryClientProvider client={queryClient}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <ThemeModeProvider>
            <SnackbarProvider anchorOrigin={{ horizontal: 'center', vertical: 'top' }}>
              <CssBaseline />
              <RouterProvider router={router} />
            </SnackbarProvider>
          </ThemeModeProvider>
        </LocalizationProvider>
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
      </QueryClientProvider>
    </PostHogProvider>
  </StrictMode>
);
